import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, SlidersHorizontal, Sparkles, X } from "lucide-react";
import ProductCard from "./components/ProductCard";
import ProductModal from "./components/ProductModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "./components/Footer";
import BannerCarousel from "./components/BannerCarousel";
import HEADERS from "./components/Headers";
import FloatingContact from "./components/FloatingContact";
import ScrollControls from "./components/ScrollControls";
import { API_BASE } from "./config";
import { scrollToElement } from "./utils/scrollUtils";
import { buildSearchParams, getSearchFromParams } from "./utils/searchUrl";
import { setHomeSeo } from "./utils/seoUtils";
import { localizeProduct } from "./utils/productLocalization";
import { trackAnalyticsEvent, trackProductSelect } from "./utils/analytics";

function normalizeType(type) {
  const value = String(type || "").normalize("NFC").trim().replace(/\s+/g, " ");
  const lower = value.toLowerCase();

  if (lower === "sofa bed") return "Sofa Bed";
  if (lower === "sofa băng") return "Sofa Băng";
  if (lower === "sofa l") return "Sofa L";
  if (lower === "sofa cong") return "Sofa Cong";
  if (lower === "sofa da") return "Sofa Da";
  if (lower === "sofa xưởng") return "Sofa Xưởng";
  if (lower === "ghế") return "Ghế";
  if (lower === "đôn") return "Đôn";
  if (lower === "giường") return "Giường";

  return value;
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
}

const PRODUCTS_PER_PAGE = 24;
const MAX_FEATURED_PRODUCTS = 5;
const SEO_LINKS = [
  {
    title: "Sofa đẹp",
    description: "Các mẫu sofa phòng khách, sofa chữ L, sofa bed và ghế bọc nệm.",
    href: "/sofa-dep",
  },
  {
    title: "Sofa phòng khách",
    description: "Tư vấn sofa theo diện tích, bố cục và phong cách phòng khách.",
    href: "/sofa-phong-khach",
  },
  {
    title: "Sofa chữ L",
    description: "Sofa góc, sofa chữ L cho căn hộ và phòng khách gia đình.",
    href: "/sofa-chu-l",
  },
  {
    title: "Sofa bed",
    description: "Sofa giường tiện dụng cho nhà nhỏ, studio và phòng đa năng.",
    href: "/sofa-bed",
  },
  {
    title: "Xưởng sofa TPHCM",
    description: "Tư vấn sofa may đo, chọn vải, chọn form và giao lắp tại nhà.",
    href: "/xuong-sofa-tphcm",
  },
  {
    title: "Sofa Vinhomes Grand Park",
    description: "Tư vấn sofa theo mặt bằng căn hộ Vinhomes Grand Park.",
    href: "/sofa-vinhomes-grand-park",
  },
  {
    title: "Sofa Thủ Đức",
    description: "Sofa phòng khách, sofa chữ L và sofa may đo tại TP. Thủ Đức.",
    href: "/sofa-thu-duc",
  },
  {
    title: "Sofa may đo",
    description: "Đặt sofa theo kích thước, màu vải và nhu cầu sử dụng riêng.",
    href: "/sofa-may-do",
  },
  {
    title: "Sofa gần đây",
    description: "Tư vấn sofa tại Hồ Chí Minh, hỗ trợ Zalo, Messenger và giao lắp.",
    href: "/sofa-gan-day",
  },
  {
    title: "Ghế sofa",
    description: "Ghế sofa bọc nệm, sofa băng, ghế đơn và đôn phối bộ.",
    href: "/ghe-sofa",
  },
  {
    title: "Giường bọc nệm",
    description: "Giường bọc nệm theo kích thước, màu vải và kiểu đầu giường.",
    href: "/giuong-boc-nem",
  },
];

function parsePageParam(value) {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

function getInitialProductPage() {
  if (typeof window === "undefined") return 1;
  return parsePageParam(new URLSearchParams(window.location.search).get("page"));
}

function getPaginationItems(currentPage, totalPages) {
  const maxVisiblePages = 7;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages]);
  const startPage = currentPage <= 3 ? 2 : Math.max(2, currentPage - 1);
  const endPage = currentPage >= totalPages - 2
    ? totalPages - 1
    : Math.min(totalPages - 1, currentPage + 1);

  for (let page = startPage; page <= endPage; page += 1) {
    pages.add(page);
  }

  const sortedPages = [...pages].sort((a, b) => a - b);

  return sortedPages.flatMap((page, index) => {
    const previousPage = sortedPages[index - 1];
    if (!previousPage || page - previousPage === 1) return [page];
    if (page - previousPage === 2) return [previousPage + 1, page];
    return [`ellipsis-${previousPage}-${page}`, page];
  });
}

function getProductId(product) {
  return String(product?._id || product?.slug || product?.name || "");
}

function getProductTimestamp(product) {
  return Date.parse(product?.createdAt || product?.updatedAt || "") || 0;
}

function sortNewestFirst(a, b) {
  return getProductTimestamp(b) - getProductTimestamp(a);
}

function limitFeaturedProducts(list) {
  return list.filter((product) => product.isFeatured).sort(sortNewestFirst).slice(0, MAX_FEATURED_PRODUCTS);
}

function sortProductsForDisplay(list) {
  return list.filter((product) => getProductId(product)).sort(sortNewestFirst);
}

export default function App() {
  const [products, setProducts] = useState([]);
  const [params] = useSearchParams();
  const urlSearch = getSearchFromParams(params);
  const [search, setSearch] = useState(urlSearch);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("Tất cả");
  const [subType, setSubType] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(getInitialProductPage);

  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const productsRef = useRef(null);
  const lastTrackedSearchRef = useRef("");

  useEffect(() => {
    setHomeSeo();
  }, []);

  useEffect(() => {
    setSearch(urlSearch);
  }, [urlSearch]);

  // 🧩 Lấy sản phẩm từ API
  useEffect(() => {
    fetch(`${API_BASE}/products`)
      .then((res) => res.json())
      .then((data) => {
        const normalizedProducts = Array.isArray(data) ? data.map((product) => localizeProduct({
          ...product,
          type: normalizeType(product.type),
        })) : [];
        setProducts(sortProductsForDisplay(normalizedProducts));
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Lỗi tải sản phẩm:", err);
        setLoading(false);
      });
  }, []);

  // 🧠 Tự động gom nhóm theo tiền tố (vd: Sofa L → nhóm Sofa)
  const groupedTypes = useMemo(() => {
    const groups = {};
    products.forEach((p) => {
      if (!p.type) return;
      const normalizedType = normalizeType(p.type);
      const mainGroup = normalizedType.split(" ")[0]; // lấy chữ đầu
      if (!groups[mainGroup]) groups[mainGroup] = new Set();
      groups[mainGroup].add(normalizedType);
    });
    Object.keys(groups).forEach(k => groups[k] = Array.from(groups[k]));
    return groups;
  }, [products]);

  const mainCategories = useMemo(() => ["Tất cả", ...Object.keys(groupedTypes)], [groupedTypes]);

  const featured = limitFeaturedProducts(products);

  // 🧩 Lọc sản phẩm theo nhóm và subType
  const normalizedSearch = normalizeSearchText(search);
  const searchTerms = normalizedSearch.split(/\s+/).filter(Boolean);
  const filtered = products
    .filter((p) => {
      if (!searchTerms.length) return true;
      const searchableText = normalizeSearchText(
        [p.name, p.type, p.material, p.description].filter(Boolean).join(" ")
      );
      return searchTerms.every((term) => searchableText.includes(term));
    })
    .filter((p) => {
      if (selectedType === "Tất cả") return true;
      const normalizedType = normalizeType(p.type);
      if (subType) return normalizedType === normalizeType(subType);
      const subTypes = groupedTypes[selectedType];
      return subTypes?.includes(normalizedType);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE));
  const paginationItems = getPaginationItems(currentPage, totalPages);
  const visibleProducts = filtered.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  useEffect(() => {
    const keyword = search.trim();
    if (keyword.length < 2 || keyword === lastTrackedSearchRef.current) return;

    const timer = setTimeout(() => {
      lastTrackedSearchRef.current = keyword;
      trackAnalyticsEvent("search", {
        search_term: keyword,
        results_count: filtered.length,
      });
    }, 900);

    return () => clearTimeout(timer);
  }, [filtered.length, search]);

  useEffect(() => {
    setCurrentPage(parsePageParam(params.get("page")));
  }, [params]);

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, loading, totalPages]);

  const goToProductPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(nextPage);
    trackAnalyticsEvent("products_page_change", {
      page_number: nextPage,
      selected_type: selectedType,
      selected_subtype: subType,
    });
    const nextParams = new URLSearchParams(params);
    if (search.trim()) nextParams.set("search", search);
    else nextParams.delete("search");
    if (nextPage > 1) nextParams.set("page", String(nextPage));
    else nextParams.delete("page");
    navigate(
      {
        pathname: "/",
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
        hash: "products",
      },
      { replace: true }
    );
    if (productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const replaceParams = useCallback((nextParams, hash = window.location.hash) => {
    navigate(
      {
        pathname: "/",
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
        hash,
      },
      { replace: true }
    );
  }, [navigate]);

  const syncSearchToUrl = useCallback((value, hash = window.location.hash) => {
    const nextParams = buildSearchParams(params, value);

    if (nextParams.toString() !== params.toString() || hash !== window.location.hash) {
      replaceParams(nextParams, hash);
    }
  }, [params, replaceParams]);

  const updateSearch = (value) => {
    setSearch(String(value || ""));
    setCurrentPage(1);
  };

  useEffect(() => {
    if (search === urlSearch) return undefined;
    const timer = setTimeout(() => syncSearchToUrl(search), 350);
    return () => clearTimeout(timer);
  }, [search, syncSearchToUrl, urlSearch]);

  const selectMainCategory = (type, shouldScroll = false) => {
    setSelectedType(type);
    setSubType(null);
    setCurrentPage(1);
    const nextParams = new URLSearchParams(params);
    nextParams.delete("page");
    if (search.trim()) nextParams.set("search", search);
    else nextParams.delete("search");
    if (type === "Tất cả") nextParams.delete("type");
    else nextParams.set("type", type);
    replaceParams(nextParams, shouldScroll ? "#products" : window.location.hash);
    trackAnalyticsEvent("filter_products", {
      filter_type: type,
      filter_level: "main_category",
    });

    if (shouldScroll) {
      setTimeout(() => {
        productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  const clearFilters = () => {
    setSelectedType("Tất cả");
    setSubType(null);
    setCurrentPage(1);
    const nextParams = new URLSearchParams(params);
    nextParams.delete("search");
    nextParams.delete("type");
    nextParams.delete("page");
    replaceParams(nextParams);
  };

  useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      if (hash === "products" && loading) return;
      setTimeout(() => scrollToElement(hash), hash === "products" ? 300 : 0);
    };

    handleHashScroll();
    window.addEventListener("hashchange", handleHashScroll);
    return () => window.removeEventListener("hashchange", handleHashScroll);
  }, [loading]);

  // Đồng bộ bộ lọc và chỉ cuộn sau khi dữ liệu sản phẩm đã render.
  useEffect(() => {
    if (loading || products.length === 0) return;

    const typeParam = params.get("type");
    if (typeParam) {
      const normalizedParam = normalizeType(typeParam);
      const main = normalizedParam.split(" ")[0];
      setSelectedType(mainCategories.includes(main) ? main : "Tất cả");
      setSubType(normalizedParam === main ? null : normalizedParam);
    } else {
      setSelectedType("Tất cả");
      setSubType(null);
    }

    if (window.location.hash === "#products") {
      const timer = setTimeout(() => scrollToElement("products"), 350);
      return () => clearTimeout(timer);
    }
  }, [loading, mainCategories, params, products]);

  // 🧩 Auto-scroll sản phẩm nổi bật
  useEffect(() => {
    if (isHovered || featured.length === 0) return;
    const totalTime = 3000;
    const updateRate = 50;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + (updateRate / totalTime) * 100;
        if (next >= 100) {
          handleAutoScroll();
          return 0;
        }
        return next;
      });
    }, updateRate);
    return () => clearInterval(interval);
  }, [isHovered, featured]);

  const handleAutoScroll = () => {
    if (!scrollRef.current) return;
    const maxScroll =
      scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
    if (scrollRef.current.scrollLeft >= maxScroll - 10) {
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = direction === "left" ? -300 : 300;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    setProgress(0);
  };

  return (
    <div className="min-h-[200vh] pb-20 scroll-smooth md:pb-0">
      {/* HEADER */}
      <HEADERS
        search={search}
        setSearch={updateSearch}
        onSearchSubmit={() => syncSearchToUrl(search, "#products")}
        onCategorySelect={(type) => selectMainCategory(type, true)}
      />

      {/* BANNER SLIDE */}
      <BannerCarousel />

      {/* SẢN PHẨM NỔI BẬT */}
      {featured.length > 0 && (
        <section
          id="featured"
          className="relative mx-auto max-w-6xl px-4 py-10 sm:py-14"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="mb-6 flex items-center justify-center gap-2 text-[var(--color-primary)]">
            <Sparkles className="h-5 w-5" />
            <h2 className="text-2xl font-bold sm:text-3xl">Sản phẩm nổi bật</h2>
          </div>

          {/* Nút điều hướng */}
          <button
            onClick={() => scroll("left")}
            aria-label="Xem sản phẩm nổi bật trước"
            className="absolute left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--color-primary)] !p-0 text-white shadow-lg transition hover:bg-[#805b42] md:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => scroll("right")}
            aria-label="Xem sản phẩm nổi bật tiếp theo"
            className="absolute right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--color-primary)] !p-0 text-white shadow-lg transition hover:bg-[#805b42] md:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          >
            {featured.map((product) => (
              <motion.div
                key={product._id}
                className="flex-shrink-0 w-[260px] sm:w-[300px] snap-start"
              >
                <ProductCard
                  product={product}
                  onClick={() =>
                    trackProductSelect(product, { item_list_name: "featured_products" })
                  }
                />
              </motion.div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full h-1 rounded-full bg-[#dfd3c8] mt-4 overflow-hidden">
            <motion.div
              className="h-full bg-[var(--color-primary)] rounded-full"
              style={{ width: `${progress}%` }}
              transition={{ ease: "linear" }}
            />
          </div>
        </section>
      )}

      {/* DANH MỤC VÀ BỘ LỌC */}
      <section className="mx-auto mt-4 max-w-6xl px-4 sm:mt-6">
        <div className="border-y border-[#e3d8ce] py-5">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[var(--color-primary)]">
                <SlidersHorizontal className="h-5 w-5" />
                <h2 className="text-xl font-bold sm:text-2xl">Danh mục sản phẩm</h2>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {loading ? "Đang tải sản phẩm..." : `${filtered.length} sản phẩm phù hợp`}
              </p>
            </div>
            {(search || selectedType !== "Tất cả") && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex min-h-11 shrink-0 items-center gap-1 px-1 text-sm font-semibold !text-[var(--color-primary)] !shadow-none"
              >
                <X className="h-4 w-4" />
                Xóa lọc
              </button>
            )}
          </div>

          <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-1 sm:flex-wrap">
          {mainCategories.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => selectMainCategory(type)}
              aria-pressed={selectedType === type}
              className={`category-button shrink-0 ${selectedType === type ? "active" : ""}`}
            >
              {type}
            </button>
          ))}
          </div>

          {selectedType !== "Tất cả" &&
            groupedTypes[selectedType] &&
            groupedTypes[selectedType].length > 1 && (
            <div className="mt-4 border-t border-[#e9dfd5] pt-4">
              <p className="mb-2 text-xs font-bold uppercase text-gray-500">
                Loại {selectedType.toLowerCase()}
              </p>
              <div className="scrollbar-hide flex gap-2 overflow-x-auto sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setSubType(null);
                    setCurrentPage(1);
                    const nextParams = new URLSearchParams(params);
                    if (search.trim()) nextParams.set("search", search);
                    else nextParams.delete("search");
                    nextParams.set("type", selectedType);
                    nextParams.delete("page");
                    replaceParams(nextParams);
                  }}
                  className={`category-button shrink-0 text-sm ${subType === null ? "active" : ""}`}
                >
                  Tất cả {selectedType.toLowerCase()}
                </button>
              {groupedTypes[selectedType].map((sub) => (
                <button
                  key={sub}
                  type="button"
                  onClick={() => {
                    setSubType(sub);
                    setCurrentPage(1);
                    const nextParams = new URLSearchParams(params);
                    if (search.trim()) nextParams.set("search", search);
                    else nextParams.delete("search");
                    nextParams.set("type", sub);
                    nextParams.delete("page");
                    replaceParams(nextParams);
                    trackAnalyticsEvent("filter_products", {
                      filter_type: sub,
                      filter_level: "sub_category",
                    });
                  }}
                  aria-pressed={subType === sub}
                  className={`category-button shrink-0 text-sm ${subType === sub ? "active" : ""}`}
                >
                  {sub}
                </button>
              ))}
              </div>
            </div>
          )}
        </div>
      </section>


      {/* DANH SÁCH SẢN PHẨM */}
      <section
        ref={productsRef}
        id="products"
        className="mx-auto max-w-6xl scroll-mt-24 px-4 py-10 sm:py-14"
      >
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="flex h-64 animate-pulse flex-col space-y-3 rounded-lg bg-white p-3 shadow sm:p-4"
                >
                  <div className="bg-gray-200 h-32 rounded-lg"></div>
                  <div className="bg-gray-200 h-4 w-3/4 rounded"></div>
                  <div className="bg-gray-200 h-4 w-1/2 rounded"></div>
                </div>
              ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-500">
            Không tìm thấy sản phẩm nào.
          </p>
        ) : (
          <>
            <motion.div
              layout
              className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8"
            >
              <AnimatePresence>
                {visibleProducts.map((product) => (
                  <motion.div
                    key={product._id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ProductCard
                      product={product}
                      onClick={() =>
                        trackProductSelect(product, { item_list_name: "products_grid" })
                      }
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => goToProductPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-11 rounded-md border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Trước
                </button>

                {paginationItems.map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      type="button"
                      onClick={() => goToProductPage(item)}
                      aria-current={currentPage === item ? "page" : undefined}
                      className={`h-11 min-w-11 rounded-md border px-3 text-sm font-semibold shadow-sm transition ${currentPage === item
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                          : "border-gray-200 bg-white text-gray-700 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                        }`}
                    >
                      {item}
                    </button>
                  ) : (
                    <span
                      key={item}
                      aria-hidden="true"
                      className="flex h-11 min-w-8 items-center justify-center px-1 text-sm font-semibold text-gray-400"
                    >
                      ...
                    </span>
                  )
                )}

                <button
                  type="button"
                  onClick={() => goToProductPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-11 rounded-md border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Sau
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section
        aria-labelledby="explore-heading"
        className="mx-auto max-w-6xl px-4 pb-12"
      >
        <div className="border-y border-[#e3d8ce] py-8 sm:py-10">
          <div className="mb-5 sm:flex sm:items-end sm:justify-between sm:gap-8">
            <div>
              <p className="text-xs font-bold uppercase text-[var(--color-primary)]">
                Danh mục tư vấn
              </p>
              <h2 id="explore-heading" className="mt-1 text-2xl font-bold sm:text-3xl">
                Khám phá theo nhu cầu
              </h2>
            </div>
            <p className="mt-2 max-w-xl text-sm leading-6 text-gray-500 sm:mt-0 sm:text-right">
              Chọn loại sản phẩm hoặc dịch vụ phù hợp với không gian của bạn.
            </p>
          </div>

          <nav
            aria-label="Khám phá sản phẩm và dịch vụ"
            className="grid grid-cols-2 border-l border-t border-[#e3d8ce] sm:grid-cols-3"
          >
          {SEO_LINKS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              aria-label={`${item.title}: ${item.description}`}
              className="group flex min-h-16 items-center justify-between gap-2 border-b border-r border-[#e3d8ce] bg-[#fffaf5] px-3 py-3 text-sm font-semibold text-gray-700 transition hover:bg-[#f7f0ea] hover:text-[var(--color-primary)] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary)] sm:min-h-20 sm:px-5 sm:text-base"
            >
              <span>{item.title}</span>
              <ArrowUpRight
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-gray-400 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--color-primary)]"
              />
            </Link>
          ))}
          </nav>
        </div>
      </section>

      <ProductModal product={selected} onClose={() => setSelected(null)} />
      <ScrollControls />
      <FloatingContact />
      {/* FOOTER */}
      <Footer />
    </div>
  );
}
